// Modal de Reconciliação de Notas Órfãs
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { AlertTriangle, ArchiveX, UserCheck, Trash2, Loader2, RefreshCw, GhostIcon } from 'lucide-react';

const API_URL = window.location.origin;

export function ReconciliarOrfaos({ open, onOpenChange, onReconciled }) {
  const [loading, setLoading] = useState(false);
  const [orfaos, setOrfaos] = useState([]);
  const [totalNotas, setTotalNotas] = useState(0);
  const [acting, setActing] = useState(null); // student_id sendo acionado
  const [alunosDestino, setAlunosDestino] = useState([]);
  const [reatribuirMap, setReatribuirMap] = useState({}); // {from_id: to_id}

  useEffect(() => {
    if (open) {
      loadOrfaos();
      loadAlunosDestino();
    }
  }, [open]);

  const loadOrfaos = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/pendencias-academicas/orfaos`);
      const data = await res.json();
      setOrfaos(data.orfaos || []);
      setTotalNotas(data.total_notas_afetadas || 0);
    } catch (e) {
      toast.error('Erro ao carregar notas órfãs');
    } finally {
      setLoading(false);
    }
  };

  const loadAlunosDestino = async () => {
    try {
      const res = await fetch(`${API_URL}/api/students?limit=500`);
      const data = await res.json();
      const arr = Array.isArray(data) ? data : (data.students || data.items || []);
      setAlunosDestino(arr.filter(a => a.id && a.name));
    } catch (e) {
      // silencioso
    }
  };

  const confirmAndAct = async (title, body, doIt) => {
    if (!window.confirm(`${title}\n\n${body}\n\nConfirma?`)) return;
    await doIt();
  };

  const handleReatribuir = async (orfao) => {
    const toId = reatribuirMap[orfao.student_id];
    if (!toId) {
      toast.error('Selecione o aluno destino');
      return;
    }
    setActing(orfao.student_id);
    try {
      const res = await fetch(`${API_URL}/api/pendencias-academicas/orfaos/reatribuir`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from_student_id: orfao.student_id, to_student_id: toId })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        await loadOrfaos();
        onReconciled?.();
      } else {
        toast.error('Erro ao reatribuir');
      }
    } catch (e) {
      toast.error('Erro ao reatribuir notas');
    } finally {
      setActing(null);
    }
  };

  const handleArquivar = (orfao) => confirmAndAct(
    'Arquivar notas órfãs',
    `Arquivar ${orfao.total_notas} nota(s) de "${orfao.student_name}"? As notas serão movidas para student_grades_arquivadas (reversível via banco).`,
    async () => {
      setActing(orfao.student_id);
      try {
        const res = await fetch(`${API_URL}/api/pendencias-academicas/orfaos/arquivar`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ student_ids: [orfao.student_id] })
        });
        const data = await res.json();
        if (data.success) {
          toast.success(data.message);
          await loadOrfaos();
          onReconciled?.();
        }
      } catch (e) {
        toast.error('Erro ao arquivar');
      } finally {
        setActing(null);
      }
    }
  );

  const handleExcluir = (orfao) => confirmAndAct(
    '⚠️ EXCLUSÃO DEFINITIVA',
    `Excluir PERMANENTEMENTE ${orfao.total_notas} nota(s) de "${orfao.student_name}"? Esta ação NÃO pode ser desfeita.`,
    async () => {
      setActing(orfao.student_id);
      try {
        const res = await fetch(`${API_URL}/api/pendencias-academicas/orfaos/excluir`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ student_ids: [orfao.student_id] })
        });
        const data = await res.json();
        if (data.success) {
          toast.success(data.message);
          await loadOrfaos();
          onReconciled?.();
        }
      } catch (e) {
        toast.error('Erro ao excluir');
      } finally {
        setActing(null);
      }
    }
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto bg-white" data-testid="modal-reconciliar-orfaos">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-800">
            <GhostIcon className="w-5 h-5 text-purple-600" />
            Reconciliar Notas Órfãs
          </DialogTitle>
          <DialogDescription>
            Notas cujo aluno não está mais matriculado/ativo. Reatribua para o aluno correto, arquive ou exclua definitivamente.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="border-purple-300 text-purple-700">
              {orfaos.length} aluno(s) órfão(s)
            </Badge>
            <Badge variant="outline" className="border-orange-300 text-orange-700">
              {totalNotas} nota(s) afetada(s)
            </Badge>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadOrfaos}
            disabled={loading}
            data-testid="btn-refresh-orfaos"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Recarregar
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : orfaos.length === 0 ? (
          <div className="text-center py-10 bg-green-50 rounded-xl border border-green-200">
            <div className="inline-flex w-12 h-12 rounded-full bg-green-100 items-center justify-center mb-3">
              <UserCheck className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-slate-700 font-medium">Nenhuma nota órfã encontrada</p>
            <p className="text-slate-500 text-sm mt-1">Todos os student_ids das notas batem com alunos matriculados.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {orfaos.map(orfao => (
              <div
                key={orfao.student_id}
                className="border border-orange-200 bg-orange-50/40 rounded-xl p-4"
                data-testid={`orfao-${orfao.student_id}`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[260px]">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertTriangle className="w-4 h-4 text-orange-600" />
                      <span className="font-semibold text-slate-800">{orfao.student_name}</span>
                      {orfao.situation && (
                        <Badge variant="outline" className="text-xs">{orfao.situation}</Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 font-mono mb-2">{orfao.student_id}</p>
                    <div className="flex flex-wrap gap-2 text-xs text-slate-600">
                      <span><strong>{orfao.total_notas}</strong> notas</span>
                      <span>•</span>
                      <span>Turmas: {orfao.turmas.map(t => t.nome).join(', ') || '—'}</span>
                      <span>•</span>
                      <span>Disciplinas: {orfao.disciplinas.slice(0, 4).join(', ')}{orfao.disciplinas.length > 4 ? '…' : ''}</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3">
                  {/* Reatribuir */}
                  <div className="flex gap-2">
                    <Select
                      value={reatribuirMap[orfao.student_id] || ''}
                      onValueChange={(v) => setReatribuirMap(m => ({ ...m, [orfao.student_id]: v }))}
                    >
                      <SelectTrigger className="h-9 text-sm bg-white" data-testid={`select-destino-${orfao.student_id}`}>
                        <SelectValue placeholder="Reatribuir para..." />
                      </SelectTrigger>
                      <SelectContent className="bg-white max-h-[300px]">
                        {alunosDestino.map(a => (
                          <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-purple-300 text-purple-700 hover:bg-purple-50"
                      onClick={() => handleReatribuir(orfao)}
                      disabled={acting === orfao.student_id}
                      data-testid={`btn-reatribuir-${orfao.student_id}`}
                    >
                      <UserCheck className="w-4 h-4" />
                    </Button>
                  </div>
                  {/* Arquivar */}
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                    onClick={() => handleArquivar(orfao)}
                    disabled={acting === orfao.student_id}
                    data-testid={`btn-arquivar-${orfao.student_id}`}
                  >
                    <ArchiveX className="w-4 h-4 mr-2" />
                    Arquivar
                  </Button>
                  {/* Excluir */}
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-300 text-red-700 hover:bg-red-50"
                    onClick={() => handleExcluir(orfao)}
                    disabled={acting === orfao.student_id}
                    data-testid={`btn-excluir-${orfao.student_id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Excluir
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
